// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import {
  AppleIcon,
  EmailIcon,
  Button,
  SvgIcon,
  Card,
  CardActions,
  CardContent,
  TextLink,
  TextField,
} from '@simlin/diagram';
import { ModelIcon } from '@simlin/diagram/ModelIcon';

import { signIn, signInWithCredentials, signUp, requestPasswordReset, resetPassword } from './auth-client';

import typography from './typography.module.css';

import styles from './Login.module.css';

// 'showEmail' collects the address; 'showPassword' signs in; 'showSignup'
// creates an account; 'showRecover' requests a reset; 'showResetConfirm' sets a
// new password from a reset token.
type EmailLoginStates = 'showEmail' | 'showPassword' | 'showSignup' | 'showRecover' | 'showResetConfirm';

export interface LoginProps {
  disabled: boolean;
  // When set (from a `?reset_token=…` password-reset email link), Login opens
  // directly on the set-new-password form with this token.
  initialResetToken?: string;
}

interface LoginState {
  emailLoginFlow: EmailLoginStates | undefined;
  email: string;
  emailError: string | undefined;
  password: string;
  passwordError: string | undefined;
  fullName: string;
  fullNameError: string | undefined;
  resetToken: string | undefined;
  infoMsg: string | undefined;
}

export const GoogleIcon: React.FunctionComponent = (props) => {
  return (
    <SvgIcon className={styles.googleIcon} {...props}>
      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
    </SvgIcon>
  );
};

// On a successful inline (email/password) sign-in the session cookie is set but
// the SPA hasn't re-bootstrapped; reloading re-runs App's session fetch, which
// lands the user on their dashboard (or onboarding). Provider sign-in navigates
// the browser itself, so it needs no explicit reload.
function reloadApp(): void {
  window.location.reload();
}

export class Login extends React.Component<LoginProps, LoginState> {
  state: LoginState;

  constructor(props: LoginProps) {
    super(props);

    // A reset-email link (?reset_token=…) opens straight on the set-new-password
    // form; otherwise show the normal provider/email options.
    this.state = {
      emailLoginFlow: props.initialResetToken ? 'showResetConfirm' : undefined,
      email: '',
      emailError: undefined,
      password: '',
      passwordError: undefined,
      fullName: '',
      fullNameError: undefined,
      resetToken: props.initialResetToken,
      infoMsg: undefined,
    };
  }

  appleLoginClick = () => {
    void signIn('apple');
  };
  googleLoginClick = () => {
    void signIn('google');
  };
  emailLoginClick = () => {
    this.setState({ emailLoginFlow: 'showEmail', infoMsg: undefined });
  };
  onFullNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ fullName: event.target.value });
  };
  onPasswordChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: event.target.value });
  };
  onEmailChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: event.target.value });
  };
  onEmailCancel = () => {
    this.setState({ emailLoginFlow: undefined, emailError: undefined, passwordError: undefined });
  };

  onSubmitEmail = () => {
    const email = this.state.email.trim();
    if (!email) {
      this.setState({ emailError: 'Enter your email address to continue' });
      return;
    }
    this.setState({ emailLoginFlow: 'showPassword', emailError: undefined });
  };

  onEmailLogin = async () => {
    const email = this.state.email.trim();
    const password = this.state.password.trim();
    if (!email) {
      this.setState({ emailError: 'Enter your email address to continue' });
      return;
    }
    if (!password) {
      this.setState({ passwordError: 'Enter your password to continue' });
      return;
    }

    const result = await signInWithCredentials(email, password);
    if (result.ok) {
      reloadApp();
      return;
    }
    this.setState({ passwordError: result.error ?? 'Invalid email or password' });
  };

  onShowSignup = () => {
    this.setState({ emailLoginFlow: 'showSignup', passwordError: undefined });
  };

  onSubmitNewUser = async () => {
    const email = this.state.email.trim();
    const fullName = this.state.fullName.trim();
    const password = this.state.password.trim();
    if (!email) {
      this.setState({ emailError: 'Enter your email address to continue' });
      return;
    }
    if (!fullName) {
      this.setState({ fullNameError: 'Enter your name to continue' });
      return;
    }
    if (!password) {
      this.setState({ passwordError: 'Enter a password to continue' });
      return;
    }

    const created = await signUp(email, password, fullName);
    if (!created.ok) {
      this.setState({ passwordError: created.error ?? 'Sign-up failed' });
      return;
    }
    // Account exists now; sign in with the same credentials and reload.
    const signedIn = await signInWithCredentials(email, password);
    if (signedIn.ok) {
      reloadApp();
      return;
    }
    this.setState({ passwordError: signedIn.error ?? 'Could not sign in after sign-up' });
  };

  onEmailHelp = () => {
    this.setState({ emailLoginFlow: 'showRecover', passwordError: undefined });
  };

  onSubmitRecovery = async () => {
    const email = this.state.email.trim();
    if (!email) {
      this.setState({ emailError: 'Enter your email address to continue' });
      return;
    }

    const result = await requestPasswordReset(email);
    if (result.token) {
      // Development: the server returned the token directly, so continue inline.
      this.setState({ emailLoginFlow: 'showResetConfirm', resetToken: result.token, password: '' });
      return;
    }
    // Production: the token would arrive by email.
    this.setState({
      emailLoginFlow: 'showPassword',
      infoMsg: 'If an account exists for that email, password reset instructions are on the way.',
    });
  };

  onSubmitResetConfirm = async () => {
    const password = this.state.password.trim();
    if (!password) {
      this.setState({ passwordError: 'Enter a new password to continue' });
      return;
    }
    const token = this.state.resetToken ?? '';
    const result = await resetPassword(token, password);
    if (result.ok) {
      this.setState({
        emailLoginFlow: 'showPassword',
        password: '',
        passwordError: undefined,
        resetToken: undefined,
        infoMsg: 'Password reset. Sign in with your new password.',
      });
      return;
    }
    this.setState({ passwordError: result.error ?? 'Could not reset password' });
  };

  onNullSubmit = (event: React.FormEvent<HTMLFormElement>): boolean => {
    event.preventDefault();
    return false;
  };

  render() {
    const disabledClass = this.props.disabled ? styles.disabled : styles.innerInner;

    let loginUI: React.JSX.Element | undefined = undefined;
    if (!this.props.disabled) {
      switch (this.state.emailLoginFlow) {
        case 'showEmail':
          loginUI = (
            <Card variant="outlined" className={styles.emailForm}>
              <form onSubmit={this.onNullSubmit}>
                <CardContent>
                  <h6 className={typography.heading6}>Sign in with email</h6>
                  <TextField
                    label="Email"
                    value={this.state.email}
                    onChange={this.onEmailChanged}
                    type="email"
                    margin="normal"
                    variant="standard"
                    error={this.state.emailError !== undefined}
                    helperText={this.state.emailError}
                    fullWidth
                    autoFocus
                  />
                </CardContent>
                <CardActions>
                  <Button style={{ marginLeft: 'auto' }} onClick={this.onEmailCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" onClick={this.onSubmitEmail}>
                    Next
                  </Button>
                </CardActions>
              </form>
            </Card>
          );
          break;
        case 'showPassword':
          loginUI = (
            <Card variant="outlined" className={styles.emailForm}>
              <form onSubmit={this.onNullSubmit}>
                <CardContent>
                  <h6 className={typography.heading6}>Sign in</h6>
                  {this.state.infoMsg ? <p className={styles.recoverInstructions}>{this.state.infoMsg}</p> : undefined}
                  <TextField
                    label="Email"
                    value={this.state.email}
                    onChange={this.onEmailChanged}
                    type="email"
                    margin="normal"
                    variant="standard"
                    error={this.state.emailError !== undefined}
                    helperText={this.state.emailError}
                    fullWidth
                  />
                  <TextField
                    label="Password"
                    value={this.state.password}
                    onChange={this.onPasswordChanged}
                    type="password"
                    autoComplete="current-password"
                    margin="normal"
                    variant="standard"
                    error={this.state.passwordError !== undefined}
                    helperText={this.state.passwordError}
                    fullWidth
                    autoFocus
                  />
                </CardContent>
                <CardActions>
                  <span className={typography.body2} style={{ marginRight: 'auto' }}>
                    <TextLink style={{ cursor: 'pointer' }} underline="hover" onClick={this.onShowSignup}>
                      Create account
                    </TextLink>
                    {' · '}
                    <TextLink style={{ cursor: 'help' }} underline="hover" onClick={this.onEmailHelp}>
                      Trouble signing in?
                    </TextLink>
                  </span>
                  <Button type="submit" variant="contained" onClick={this.onEmailLogin}>
                    Sign in
                  </Button>
                </CardActions>
              </form>
            </Card>
          );
          break;
        case 'showSignup':
          loginUI = (
            <Card variant="outlined" className={styles.emailForm}>
              <form onSubmit={this.onNullSubmit}>
                <CardContent>
                  <h6 className={typography.heading6}>Create account</h6>
                  <TextField
                    label="Email"
                    value={this.state.email}
                    onChange={this.onEmailChanged}
                    type="email"
                    margin="normal"
                    variant="standard"
                    error={this.state.emailError !== undefined}
                    helperText={this.state.emailError}
                    fullWidth
                  />
                  <TextField
                    label="First & last name"
                    value={this.state.fullName}
                    onChange={this.onFullNameChanged}
                    margin="normal"
                    variant="standard"
                    error={this.state.fullNameError !== undefined}
                    helperText={this.state.fullNameError}
                    fullWidth
                    autoFocus
                  />
                  <TextField
                    label="Choose password"
                    value={this.state.password}
                    onChange={this.onPasswordChanged}
                    type="password"
                    autoComplete="new-password"
                    margin="normal"
                    variant="standard"
                    error={this.state.passwordError !== undefined}
                    helperText={this.state.passwordError}
                    fullWidth
                  />
                </CardContent>
                <CardActions>
                  <Button style={{ marginLeft: 'auto' }} onClick={this.onEmailCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" onClick={this.onSubmitNewUser}>
                    Save
                  </Button>
                </CardActions>
              </form>
            </Card>
          );
          break;
        case 'showRecover':
          loginUI = (
            <Card variant="outlined" className={styles.emailForm}>
              <form onSubmit={this.onNullSubmit}>
                <CardContent>
                  <h6 className={typography.heading6}>Recover password</h6>
                  <p className={styles.recoverInstructions}>
                    Enter your email and we'll send instructions to reset your password
                  </p>
                  <TextField
                    label="Email"
                    value={this.state.email}
                    onChange={this.onEmailChanged}
                    type="email"
                    margin="normal"
                    variant="standard"
                    error={this.state.emailError !== undefined}
                    helperText={this.state.emailError}
                    fullWidth
                    autoFocus
                  />
                </CardContent>
                <CardActions>
                  <Button style={{ marginLeft: 'auto' }} onClick={this.onEmailCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" onClick={this.onSubmitRecovery}>
                    Send
                  </Button>
                </CardActions>
              </form>
            </Card>
          );
          break;
        case 'showResetConfirm':
          loginUI = (
            <Card variant="outlined" className={styles.emailForm}>
              <form onSubmit={this.onNullSubmit}>
                <CardContent>
                  <h6 className={typography.heading6}>Set a new password</h6>
                  <TextField
                    label="New password"
                    value={this.state.password}
                    onChange={this.onPasswordChanged}
                    type="password"
                    autoComplete="new-password"
                    margin="normal"
                    variant="standard"
                    error={this.state.passwordError !== undefined}
                    helperText={this.state.passwordError}
                    fullWidth
                    autoFocus
                  />
                </CardContent>
                <CardActions>
                  <Button style={{ marginLeft: 'auto' }} onClick={this.onEmailCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" onClick={this.onSubmitResetConfirm}>
                    Save
                  </Button>
                </CardActions>
              </form>
            </Card>
          );
          break;
        default:
          loginUI = (
            <div className={styles.optionsButtons}>
              <Button
                variant="contained"
                className={styles.appleButton}
                startIcon={<AppleIcon />}
                onClick={this.appleLoginClick}
              >
                Sign in with Apple
              </Button>
              <Button variant="contained" color="primary" startIcon={<GoogleIcon />} onClick={this.googleLoginClick}>
                Sign in with Google
              </Button>
              <Button
                variant="contained"
                className={styles.emailButton}
                startIcon={<EmailIcon />}
                onClick={this.emailLoginClick}
              >
                Sign in with email
              </Button>
              {/* Anonymous "play in-session, can't save" (acceptance §1.7): the
                  backend-free demo editor at /demo.html lets a first-time visitor
                  build and simulate a model without an account (save routes to a
                  local download). Link to the .html file directly so the SPA
                  history fallback doesn't intercept a bare /demo. */}
              <p className={typography.body2} style={{ textAlign: 'center', marginTop: 8 }}>
                <TextLink
                  style={{ cursor: 'pointer' }}
                  underline="hover"
                  onClick={() => {
                    window.location.href = '/demo.html';
                  }}
                >
                  Try it without signing in →
                </TextLink>
              </p>
            </div>
          );
      }
    }

    return (
      <div className={styles.outer}>
        <div className={styles.middle}>
          <div className={styles.inner}>
            <ModelIcon className={styles.logo} />
            <h1 className={styles.brand}>Systems Analyzer</h1>
            <p className={styles.tagline}>Understand Systems. Drive Change. Create Coherence.</p>
            {/* D7.4: brief value prop so a first-time visitor knows what this is
                before signing in. Copy mirrors the tagline's three verbs; kept
                minimal and brand-aligned for Williams to edit. */}
            <div className={styles.highlights}>
              <div className={styles.highlight}>
                <h3 className={styles.highlightTitle}>Understand systems</h3>
                <p className={styles.highlightBody}>Build stock-and-flow models that make complex dynamics clear.</p>
              </div>
              <div className={styles.highlight}>
                <h3 className={styles.highlightTitle}>Drive change</h3>
                <p className={styles.highlightBody}>Simulate strategy and policy before you commit in the real world.</p>
              </div>
              <div className={styles.highlight}>
                <h3 className={styles.highlightTitle}>Create coherence</h3>
                <p className={styles.highlightBody}>Iterate and learn faster, with fewer costs and the freedom to fail.</p>
              </div>
            </div>
            <div className={disabledClass}>{loginUI}</div>
          </div>
        </div>
      </div>
    );
  }
}
